import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, Ban, Calendar, AlertCircle } from 'lucide-react';
import { DayEventsResponse, DayEvent } from '@/types/billing-calendar';

interface DayEventsListProps {
  dayEvents: DayEventsResponse | null;
  onBlockDay?: () => void;
  onRescheduleEvent?: (event: DayEvent) => void;
  onCancelReschedule?: (rescheduleId: number) => void;
  loading?: boolean;
}

const DayEventsList: React.FC<DayEventsListProps> = ({
  dayEvents,
  onBlockDay,
  onRescheduleEvent,
  onCancelReschedule,
  loading
}) => {
  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-gray-500">Carregando eventos...</div>
        </CardContent>
      </Card>
    );
  }

  if (!dayEvents) {
    return (
      <div className="text-center py-8">
        <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">Selecione um dia no calendário para ver os eventos</p>
      </div>
    );
  }

  const { day, month, year, isBlocked, events, blockedReason, isHoliday, holidayName } = dayEvents;

  const formatTime = (time?: string) => {
    if (!time) return 'Não configurado';
    const [hours, minutes] = time.split(':');
    return `${hours}:${minutes}`;
  };

  const getEventBadgeColor = (event: string) => {
    switch (event) {
      case 'COBRANÇA':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'REVISTORIA':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'REATIVAÇÃO':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'RECUPERAÇÃO':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div>
        {isHoliday && (
          <div className="mb-4 p-3 bg-yellow-50 border border-orange-200 rounded-lg">
            <div className="flex items-center gap-2 text-orange-700">
              <Calendar className="h-4 w-4" />
              <span className="font-semibold">Feriado Nacional</span>
            </div>
            {holidayName && (
              <p className="text-sm text-orange-600 mt-1 font-medium">{holidayName}</p>
            )}
            <p className="text-xs text-orange-600 mt-1">
              Este é um dia não útil. Considere reagendar eventos para um dia útil.
            </p>
          </div>
        )}

        {isBlocked && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-4 w-4" />
              <span className="font-semibold">Dia bloqueado</span>
            </div>
            {blockedReason && (
              <p className="text-sm text-red-600 mt-1">{blockedReason}</p>
            )}
            <p className="text-xs text-red-600 mt-1">
              Nenhum evento será executado neste dia.
            </p>
          </div>
        )}

        {events.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            {isBlocked ? (
              <p>Nenhum evento configurado para este dia (dia bloqueado)</p>
            ) : (
              <p>Nenhum evento configurado para este dia</p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {events.map((event) => (
              <div
                key={event.id}
                className={`
                  border rounded-lg p-4 transition-all duration-200
                  ${event.isRescheduled 
                    ? 'bg-blue-50 border-blue-300' 
                    : isBlocked 
                      ? 'bg-gray-50 border-gray-200 opacity-75' 
                      : 'bg-white border-gray-200 hover:shadow-md'
                  }
                `}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Badge className={getEventBadgeColor(event.event)}>
                      {event.event}
                    </Badge>
                    {event.isRescheduled && (
                      <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">
                        Reagendado
                      </Badge>
                    )}
                  </div>
                  {!isBlocked && !event.isRescheduled && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onRescheduleEvent?.(event)}
                      className="text-xs"
                    >
                      Reagendar
                    </Button>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-700">
                    <span className="font-medium">Dia original:</span>
                    <span className="text-gray-600">
                      Dia {event.original_day}
                    </span>
                    {event.isRescheduled && (
                      <span className="text-blue-600 font-semibold">
                        → Reagendado para dia {event.rescheduleInfo?.rescheduled_day}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 text-gray-700">
                    <span className="font-medium">Vencimento:</span>
                    <span className="text-gray-600">Dia {event.due_day}</span>
                  </div>

                  <div className="flex items-center gap-2 text-gray-700">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <span className="font-medium">Horário:</span>
                    <span className="text-gray-600">{formatTime(event.window_start)}</span>
                  </div>

                  <div className="flex items-center gap-2 text-gray-700">
                    <span className="font-medium">Mês de cobrança:</span>
                    <span className="text-gray-600">{event.billing_month}</span>
                  </div>

                  {event.isRescheduled && event.rescheduleInfo?.reason && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                      <span className="font-medium text-blue-700">Motivo do reagendamento:</span>
                      <p className="text-blue-600 mt-1">{event.rescheduleInfo.reason}</p>
                    </div>
                  )}

                  {event.isRescheduled && event.rescheduleInfo && onCancelReschedule && (
                    <div className="mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onCancelReschedule(event.rescheduleInfo!.id)}
                        className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        Cancelar Reagendamento
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

export default DayEventsList;

